import React, { useEffect, useState } from "react";
import api from "../utils/api.js";
import { URLS, ImgURL } from "../utils/urls.js";
import { Search, User, CreditCard, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { useTopLoader } from "../context/TopLoaderContext";
import Tooltip from "../components/Tooltip.jsx";

const TransactionsPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const { start, stop } = useTopLoader();

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        start();
        setLoading(true);
        const res = await api.get(URLS.GET_TRANSACTIONS, {
          params: { 
            page: currentPage,
            limit: limit, 
            searchQuery: searchQuery 
          }
        });
        setTransactions(res.data.transactions || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalCount(res.data.totalCount || 0);
      } catch (err) {
        console.error("Failed to fetch transactions:", err);
      } finally {
        setLoading(false);
        stop();
      }
    };
    fetchTransactions();
  }, [currentPage, limit, searchQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      let startRange = Math.max(2, currentPage - 1);
      let endRange = Math.min(totalPages - 1, currentPage + 1);
      
      if (currentPage <= 2) {
        endRange = 3;
      }
      if (currentPage >= totalPages - 1) {
        startRange = totalPages - 2;
      }
      
      if (startRange > 2) {
        pages.push("...");
      }
      
      for (let i = startRange; i <= endRange; i++) {
        pages.push(i);
      }
      
      if (endRange < totalPages - 1) {
        pages.push("...");
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-3xl font-bold text-gray-800">Transactions & Billing</h2>
          <p className="text-gray-500">Overview of all event ticket purchases and premium subscriptions</p>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search transactions..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="py-2 pr-10 pl-4 rounded-full border border-gray-300 focus:ring-2 focus:ring-[#db8654] focus:border-transparent transition-all shadow-sm"
          />
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col">
        {loading ? (
          <div className="p-12 text-center text-gray-500">Loading transaction logs...</div>
        ) : (
          <>
            <table className="w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="p-4 text-xs font-semibold uppercase tracking-wider">User</th>
                  <th className="p-4 text-xs font-semibold uppercase tracking-wider">Type</th>
                  <th className="p-4 text-xs font-semibold uppercase tracking-wider">Description</th>
                  <th className="p-4 text-xs font-semibold uppercase tracking-wider">Amount</th>
                  <th className="p-4 text-xs font-semibold uppercase tracking-wider">Date</th>
                  <th className="p-4 text-xs font-semibold uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {transactions.length > 0 ? (
                  transactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50 transition">
                      <td className="p-4 flex items-center gap-3">
                        {tx.profilePhotoUrl ? (
                          <img className="h-10 w-10 rounded-full object-cover" src={ImgURL(tx.profilePhotoUrl)} alt="" />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400"><User size={20} /></div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900">{tx.firstName || 'Unknown'} {tx.lastName || ''}</p>
                          <p className="text-sm text-gray-500">{tx.email || 'N/A'}</p>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          tx.type === 'event_ticket' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {tx.type === 'event_ticket' ? 'Event Ticket' : 'Subscription'}
                        </span>
                      </td>
                      <td className="p-4 text-gray-600 font-semibold">
                        {tx.type === 'event_ticket' ? (tx.eventTitle || `Event Ticket (${tx.targetId})`) : 'Premium Subscription'}
                      </td>
                      <td className="p-4 text-gray-900 font-bold">
                        ${Number(tx.amount).toFixed(2)} <span className="text-xs text-gray-500 font-normal">{tx.currency}</span>
                      </td>
                      <td className="p-4 text-gray-600">
                        {tx.createdAt ? new Date(tx.createdAt).toLocaleString() : 'N/A'}
                      </td>
                      <td className="p-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          tx.paymentStatus === 'PAID' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {tx.paymentStatus || 'PENDING'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="p-8 text-center text-gray-500">No transactions found.</td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Advanced Pagination Controls */}
            <div className="bg-white border-t border-gray-200 px-6 py-4 flex items-center justify-between flex-wrap gap-4 select-none">
              {/* Info text & Page Size Selector */}
              <div className="flex items-center gap-4 text-sm text-gray-700">
                <div>
                  Showing <span className="font-semibold">{totalCount === 0 ? 0 : (currentPage - 1) * limit + 1}</span> to{" "}
                  <span className="font-semibold">{Math.min(currentPage * limit, totalCount)}</span> of{" "}
                  <span className="font-semibold">{totalCount}</span> transactions
                </div>
                
                <div className="flex items-center gap-2">
                  <span>Show</span>
                  <select
                    value={limit}
                    onChange={handleLimitChange}
                    className="border border-gray-300 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-[#db8654] focus:border-transparent cursor-pointer text-gray-800"
                  >
                    {[5, 10, 25, 50, 100].map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Numeric Page buttons */}
              <div className="flex items-center gap-1">
                {/* First Page */}
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg border transition ${
                    currentPage === 1
                      ? "bg-gray-50 border-gray-200 text-gray-300 cursor-not-allowed"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                  }`}
                >
                  <ChevronsLeft size={16} />
                </button>

                {/* Prev Page */}
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`p-2 rounded-lg border transition ${
                    currentPage === 1
                      ? "bg-gray-50 border-gray-200 text-gray-300 cursor-not-allowed"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                  }`}
                >
                  <ChevronLeft size={16} />
                </button>

                {/* Page numbers */}
                {getPageNumbers().map((page, index) => {
                  if (page === "...") {
                    return (
                      <span key={`ell-${index}`} className="px-3 py-2 text-gray-400">
                        ...
                      </span>
                    );
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 rounded-lg border text-sm font-semibold transition ${
                        currentPage === page
                          ? "bg-[#db8654] border-[#db8654] text-white animate-pulse-once"
                          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}

                {/* Next Page */}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg border transition ${
                    currentPage === totalPages
                      ? "bg-gray-50 border-gray-200 text-gray-300 cursor-not-allowed"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                  }`}
                >
                  <ChevronRight size={16} />
                </button>

                {/* Last Page */}
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className={`p-2 rounded-lg border transition ${
                    currentPage === totalPages
                      ? "bg-gray-50 border-gray-200 text-gray-300 cursor-not-allowed"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                  }`}
                >
                  <ChevronsRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default TransactionsPage;
